/**
 * 兑换码校验和兑换Hook
 * 提供前端格式校验和Mock后端兑换逻辑
 */

import { useCallback, useContext, useState } from "react";

import log from "logbeacon";
import { useSnapshot } from "valtio";
import { capitalizeString } from "../utils/string";
import { Reward } from "./useQueryRewards";
import { MockRedeemCodeService } from "@/src/services/mockRedeemCodeService";
import { OspApiClientContext } from "@/src/features";
import { rootStore } from "@/src/store";

interface ValidationResult {
  isValid: boolean;
  error?: "INVALID_FORMAT";
}

export interface RedeemResult {
  success: boolean;
  rewards: Reward[];
  errorCode?: number;
}

const REDEEM_CODE_REGEX = /^[A-Za-z0-9][A-Za-z0-9\-_]*$/;

export interface UseRedeemCodeResult {
  validateCodeFormat: (code: string) => ValidationResult;
  redeemCode: (code: string) => Promise<RedeemResult>;
  isLoading: boolean;
  errorCode?: number;
}

interface RedeemEquityMember {
  productId: string;
  productName: string;
  effectivePeriodsUnit: string;
  effectivePeriodsCount: number;
}

interface RedeemEquityAura {
  auraFirstAmount: number;
}

const USE_MOCK_DATA = false;

export const useRedeemCode = (): UseRedeemCodeResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<number | null>(null);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { memberApi } = useContext(OspApiClientContext);

  const validateCodeFormat = useCallback((code: string): ValidationResult => {
    setError(null);

    if (!code || code.trim() === "") {
      return { isValid: false, error: "INVALID_FORMAT" };
    }

    if (!REDEEM_CODE_REGEX.test(code.trim())) {
      return { isValid: false, error: "INVALID_FORMAT" };
    }

    return { isValid: true };
  }, []);

  const redeemCode = useCallback(
    async (code: string): Promise<RedeemResult> => {
      setIsLoading(true);
      setError(null);
      log.info("[useRedeemCode] Starting redeem process", { code });
      const ownerId = ownerInfo?.id;
      if (!ownerId) {
        log.error("[useRedeemCode] User not logged in");
        throw new Error("User not logged in");
      }
      let result;
      if (USE_MOCK_DATA) {
        log.info("[useRedeemCode] Using mock data for redeem", { code });
        result = await MockRedeemCodeService.redeemCode(code, ownerId);
      } else {
        log.info("[useRedeemCode] Using real API for redeem", { code });
        if (!memberApi) {
          throw new Error("API client not available");
        }
        try {
          const response = await memberApi.member.redeemReward(code);
          if (response.data?.redeem_code) {
            const rewards: Reward[] = [];
            const reward = response.data.redeem_code;
            if (reward.redeemed_type === "MEMBER") {
              const member = reward.redeemed_equity as RedeemEquityMember;
              rewards.push({
                type: "REWARDS",
                value: `${member?.productName} ${member?.effectivePeriodsCount} ${capitalizeString(member?.effectivePeriodsUnit)}${member?.effectivePeriodsCount > 1 ? "s" : ""}`,
                name: "Member",
                orderId: `member-${Date.now()}`,
              });
            }

            if (reward.redeemed_type === "AURA") {
              const aura = reward.redeemed_equity as RedeemEquityAura;

              rewards.push({
                type: "AURA",
                name: "Aura",
                value: aura?.auraFirstAmount.toString(),
                orderId: `aura-${Date.now()}`,
              });
            }

            result = {
              success: true,
              data: {
                rewards,
                memberInfo: {
                  codeType: reward.code?.substring(0, 3),
                  redeemedAt: new Date(
                    reward.redeemed_time || Date.now(),
                  ).toISOString(),
                },
              },
            };
          } else {
            result = {
              success: false,
              error: "error",
              errorCode: response?.error?.code,
            };
            log.error("[文案]----->", response);
          }
        } catch (apiError: any) {
          result = {
            success: false,
            errorCode: apiError?.error?.code,
          };
        }
      }
      setIsLoading(false);
      if (result.success && result.data) {
        log.info("[useRedeemCode] Redeem successful", {
          code,
          rewards: result.data.rewards,
        });
        return {
          success: true,
          rewards: result.data.rewards,
        };
      } else {
        setError(result.errorCode);
        return {
          success: false,
          rewards: [],
          errorCode: result.errorCode,
        };
      }
    },
    [ownerInfo?.id, memberApi],
  );

  return {
    validateCodeFormat,
    redeemCode,
    isLoading,
    errorCode: error || undefined,
  };
};
